import { useState, useCallback } from 'react';
import { Gavel, Send, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { callRpc } from '@/lib/supabaseTyped';
import { linkModerationToAppeal } from '@/hooks/useModerationStrikes';

type Status = 'idle' | 'sending' | 'success' | 'error';

export interface SubmitAppealDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Ban-state som visas till user. */
  banState?: string;
  /** Ban-reason från backend, read-only text. */
  banReason?: string;
  /**
   * Om appeal:en triggas från ett specifikt moderation-event (klick på
   * "Överklaga" i ModerationFlagMessage), passar vi in event-id + rule
   * + correlation så vi kan (a) visa kontexten för user, (b) länka via
   * link_moderation_to_appeal efter submit.
   */
  moderationEventId?: string;
  moderationRuleId?: string;
  moderationCorrelationId?: string;
}

/**
 * User-facing dialog för banned users att skicka in appeal.
 * Triggas från AuthContext.signIn när 'BANNED:reason' kastas.
 */
export function SubmitAppealDialog({
  open, onOpenChange, banState, banReason,
  moderationEventId, moderationRuleId, moderationCorrelationId,
}: SubmitAppealDialogProps) {
  const [message, setMessage] = useState('');
  const [status, setStatus] = useState<Status>('idle');
  const [resultMsg, setResultMsg] = useState('');

  const submit = useCallback(async () => {
    const trimmed = message.trim();
    if (trimmed.length < 10) {
      setStatus('error');
      setResultMsg('Appealen måste vara minst 10 tecken.');
      return;
    }
    setStatus('sending');
    setResultMsg('');

    // Bygg en appeal-text som inkluderar moderation-kontext om tillgänglig,
    // så admin ser vad user överklagar utan att behöva joina tabellerna.
    const prefixLines: string[] = [];
    if (moderationRuleId) prefixLines.push(`[Regel: ${moderationRuleId}]`);
    if (moderationCorrelationId) prefixLines.push(`[Correlation: ${moderationCorrelationId}]`);
    const appealMessage = prefixLines.length
      ? `${prefixLines.join(' ')}\n\n${trimmed}`
      : trimmed;

    const { data, error } = await callRpc<{ success: boolean; error?: string; appeal_id?: string }>(
      'submit_my_appeal',
      { appeal_message: appealMessage },
    );

    if (error) {
      setStatus('error');
      setResultMsg(error.message || 'Submission failed.');
      return;
    }
    if (!data?.success) {
      setStatus('error');
      setResultMsg(data?.error ?? 'Submission failed.');
      return;
    }

    // Länka moderation-event till appeal om caller gav oss event-id.
    if (moderationEventId && data.appeal_id) {
      await linkModerationToAppeal(moderationEventId, data.appeal_id);
    }

    setStatus('success');
    setResultMsg(`Appeal inskickad. Vi svarar inom SLA (typiskt 3-14 dagar).`);
    setMessage('');
  }, [message, moderationEventId, moderationRuleId, moderationCorrelationId]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Gavel className="w-4 h-4" />
            {moderationEventId ? 'Överklaga flaggning' : 'Överklaga ban'}
          </DialogTitle>
          <DialogDescription>
            {moderationEventId
              ? 'Ditt meddelande flaggades av moderationssystemet. Du kan begära en manuell granskning.'
              : `Ditt konto är för närvarande ${banState ? `i status "${banState}"` : 'begränsat'}. Du kan skicka in en appeal som granskas av admin.`}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {moderationRuleId && (
            <div className="text-xs border rounded p-2 bg-muted/40 space-y-1">
              <div className="text-muted-foreground">Regel som triggade:</div>
              <div className="font-mono">{moderationRuleId}</div>
              {moderationCorrelationId && (
                <div className="text-muted-foreground text-[10px] mt-1">
                  Correlation: <span className="font-mono">{moderationCorrelationId}</span>
                </div>
              )}
            </div>
          )}
          {banReason && !moderationEventId && (
            <div className="text-xs border rounded p-2 bg-muted/40">
              <div className="text-muted-foreground mb-1">Ban-orsak:</div>
              <div className="whitespace-pre-wrap">{banReason}</div>
            </div>
          )}

          <div>
            <Label className="text-xs">Ditt meddelande (min 10 tecken)</Label>
            <Textarea
              value={message}
              onChange={e => setMessage(e.target.value)}
              rows={5}
              placeholder="Beskriv varför du anser att banet ska hävas eller reduceras. Var specifik och saklig."
            />
          </div>

          {status === 'error' && (
            <div className="flex gap-2 text-xs text-destructive items-start">
              <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>{resultMsg}</span>
            </div>
          )}
          {status === 'success' && (
            <div className="flex gap-2 text-xs text-green-600 items-start">
              <CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>{resultMsg}</span>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>
              {status === 'success' ? 'Stäng' : 'Avbryt'}
            </Button>
            {status !== 'success' && (
              <Button
                size="sm"
                onClick={submit}
                disabled={status === 'sending' || message.trim().length < 10}
              >
                <Send className="w-3.5 h-3.5 mr-1" />
                {status === 'sending' ? 'Skickar…' : 'Skicka appeal'}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
